import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, Download, ImageOff, Mail, TriangleAlert } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Select } from "../components/ui/Select/Select";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { Input } from "../components/ui/Input/Input";
import { Button } from "../components/ui/Button/Button";
import { FormField } from "../components/ui/FormField/FormField";
import { Modal } from "../components/ui/Modal/Modal";
import { EmptyState } from "../components/ui/EmptyState/EmptyState";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { localizedCountryName } from "../lib/country-names";
import { localizedSpecialDayName } from "@crm/shared/special-day-names";
import {
	useSpecialDays,
	useContactCountries,
	designDownloadUrl,
	designPreviewUrl,
	useSendSpecialDayEmail,
	useSpecialDayRecipients,
	type SendSpecialDayEmailResult,
	type SpecialDay,
	type SpecialDayDesign,
} from "../hooks/use-special-days";
import styles from "./SpecialDays.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/special-days",
	component: SpecialDaysPage,
});

const RANGE_OPTIONS = ["0", "3", "7", "15", "30", "90", "365"];

function toIsoDate(value: Date): string {
	return value.toISOString().slice(0, 10);
}

function addDays(value: string, days: number): string {
	const date = new Date(`${value}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + days);
	return toIsoDate(date);
}

function flagEmoji(code: string): string {
	const upper = code.toUpperCase();
	if (upper.length !== 2) return "";
	const first = upper.codePointAt(0);
	const second = upper.codePointAt(1);
	if (first === undefined || second === undefined) return "";
	return String.fromCodePoint(0x1f1e6 + first - 65, 0x1f1e6 + second - 65);
}

function groupByDate(days: readonly SpecialDay[]): [string, SpecialDay[]][] {
	const groups = new Map<string, SpecialDay[]>();
	for (const day of days) {
		const existing = groups.get(day.date);
		if (existing) existing.push(day);
		else groups.set(day.date, [day]);
	}
	return [...groups.entries()];
}

function SpecialDaysPage() {
	const { t, i18n } = useTranslation();
	const today = useMemo(() => toIsoDate(new Date()), []);
	const [rangeDays, setRangeDays] = useState("0");
	const [country, setCountry] = useState("");
	const [search, setSearch] = useState("");
	const [mailDay, setMailDay] = useState<SpecialDay | null>(null);

	const countriesQuery = useContactCountries();
	const filters = useMemo(
		() => ({
			from: today,
			to: addDays(today, Number(rangeDays)),
			countries: country ? [country] : undefined,
		}),
		[today, rangeDays, country],
	);
	const { data, isLoading, error } = useSpecialDays(filters);

	const dateFormatter = useMemo(
		() =>
			new Intl.DateTimeFormat(i18n.language, {
				day: "numeric",
				month: "long",
				weekday: "long",
			}),
		[i18n.language],
	);

	const filtered = useMemo(() => {
		const days = data?.days ?? [];
		const term = search.trim().toLocaleLowerCase(i18n.language);
		if (!term) return days;
		return days.filter((day) => {
			const countryName = localizedCountryName(day.countryCode, null, i18n.language);
			const localName = localizedSpecialDayName(day.name, i18n.language);
			return (
				day.name.toLocaleLowerCase(i18n.language).includes(term) ||
				localName.toLocaleLowerCase(i18n.language).includes(term) ||
				countryName.toLocaleLowerCase(i18n.language).includes(term)
			);
		});
	}, [data, search, i18n.language]);

	const grouped = useMemo(() => groupByDate(filtered), [filtered]);

	const countryOptions = useMemo(() => {
		const items = countriesQuery.data?.items ?? [];
		const entries: { value: string; label: string; name: string }[] = [];
		for (const item of items) {
			if (!item.supported) continue;
			const name = localizedCountryName(item.code, null, i18n.language);
			entries.push({
				value: item.code,
				label: `${flagEmoji(item.code)} ${name} (${item.companyCount})`,
				name,
			});
		}
		const collator = new Intl.Collator(i18n.language, { sensitivity: "base" });
		entries.sort((a, b) => collator.compare(a.name, b.name));
		return [
			{ value: "", label: t("specialDays.allCountries") },
			...entries.map(({ value, label }) => ({ value, label })),
		];
	}, [countriesQuery.data, i18n.language, t]);

	// const unsupported = useMemo(
	// 	() => (countriesQuery.data?.items ?? []).filter((item) => !item.supported),
	// 	[countriesQuery.data],
	// );

	return (
		<div className={styles.page}>
			<PageHeader title={t("specialDays.title")} subtitle={t("specialDays.subtitle")} />

			{data?.nextcloud.configured === false && (
				<div className={styles.notice}>
					<TriangleAlert size={16} />
					<span>{t("specialDays.nextcloudMissing")}</span>
				</div>
			)}
			{data?.nextcloud.baseMissing && (
				<div className={styles.notice}>
					<TriangleAlert size={16} />
					<span>{t("specialDays.baseMissing", { path: data.base })}</span>
				</div>
			)}
			{data?.nextcloud.error && (
				<div className={styles.noticeDanger}>
					<TriangleAlert size={16} />
					<span>{data.nextcloud.error}</span>
				</div>
			)}

			<div className={styles.filters}>
				<div className={styles.filterControl}>
					<SearchableSelect
						options={countryOptions}
						value={country}
						onChange={(value) => setCountry(value)}
						placeholder={t("specialDays.allCountries")}
						clearable
					/>
				</div>
				<Select
					options={RANGE_OPTIONS.map((value) => ({
						value,
						label:
							value === "0"
								? t("specialDays.today")
								: t("specialDays.nextDays", { count: Number(value) }),
					}))}
					value={rangeDays}
					onChange={(e) => setRangeDays(e.target.value)}
					className={styles.filterControl}
				/>
				<Input
					placeholder={t("specialDays.searchPlaceholder")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className={styles.filterControl}
				/>
			</div>

			{error ? (
				<Card className={styles.quietCard ?? ""}>{(error as Error).message}</Card>
			) : isLoading ? (
				<LoadingSpinner />
			) : grouped.length === 0 ? (
				<EmptyState
					message={rangeDays === "0" ? t("specialDays.noneToday") : t("specialDays.noUpcoming")}
				/>
			) : (
				<div className={styles.groups}>
					{grouped.map(([date, days]) => (
						<div key={date} className={styles.group}>
							<div className={styles.groupHeader}>
								<CalendarDays size={16} className={styles.groupIcon} />
								<span className={styles.groupDate}>
									{date === today
										? t("specialDays.today")
										: dateFormatter.format(new Date(`${date}T00:00:00`))}
								</span>
								<span className={styles.groupCount}>
									{t("specialDays.dayCount", { count: days.length })}
								</span>
							</div>
							<div className={styles.cardGrid}>
								{days.map((day) => (
									<DayCard
										key={`${day.countryCode}-${day.designKey}`}
										day={day}
										lang={i18n.language}
										onMail={() => setMailDay(day)}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			)}

			{mailDay?.design && (
				<GreetingMailModal
					day={mailDay}
					design={mailDay.design}
					lang={i18n.language}
					onClose={() => setMailDay(null)}
				/>
			)}

			{/* {unsupported.length > 0 && (
				<Card className={styles.unsupportedCard ?? ""}>
					<strong>{t("specialDays.unsupportedTitle")}</strong>
					<p className={styles.unsupportedText}>
						{unsupported
							.map((item) => localizedCountryName(item.code, null, i18n.language))
							.join(", ")}
					</p>
				</Card>
			)} */}
		</div>
	);
}

function DayCard({ day, lang, onMail }: { day: SpecialDay; lang: string; onMail: () => void }) {
	const { t } = useTranslation();
	const countryName = localizedCountryName(day.countryCode, null, lang);
	const dayName = localizedSpecialDayName(day.name, lang);
	return (
		<Card className={styles.dayCard ?? ""}>
			<div className={styles.preview}>
				{day.design ? (
					<img
						src={designPreviewUrl(day.design.path)}
						alt={dayName}
						className={styles.previewImage}
						loading="lazy"
					/>
				) : (
					<div className={styles.previewMissing}>
						<ImageOff size={26} />
						<span>{t("specialDays.noDesign")}</span>
						<code className={styles.expectedName}>{day.expectedPath}</code>
					</div>
				)}
			</div>
			<div className={styles.cardBody}>
				<div className={styles.cardCountry}>
					<span className={styles.flag}>{flagEmoji(day.countryCode)}</span>
					{countryName}
					{day.type !== "public" && (
						<span className={styles.typeBadge}>{t(`specialDays.type.${day.type}`)}</span>
					)}
				</div>
				<div className={styles.cardName}>{dayName}</div>
				{day.design ? (
					<div className={styles.actionRow}>
						<a className={styles.downloadButton} href={designDownloadUrl(day.design.path)} download>
							<Download size={16} />
							{t("specialDays.download")}
							<span className={styles.langTag}>
								{day.design.language}
								{day.design.isFallback ? ` · ${t("specialDays.fallback")}` : ""}
							</span>
						</a>
						<button
							type="button"
							className={styles.mailButton}
							onClick={onMail}
							title={t("specialDays.sendMail")}
							aria-label={t("specialDays.sendMail")}
						>
							<Mail size={16} />
						</button>
					</div>
				) : (
					<>
						<div className={styles.actionRow}>
							<button type="button" className={styles.downloadButton} disabled>
								<Download size={16} />
								{t("specialDays.download")}
							</button>
							<button
								type="button"
								className={styles.mailButton}
								disabled
								aria-label={t("specialDays.sendMail")}
							>
								<Mail size={16} />
							</button>
						</div>
						<span className={styles.missingNote}>{t("specialDays.noDesignHint")}</span>
					</>
				)}
			</div>
		</Card>
	);
}

function defaultGreeting(day: SpecialDay): { subject: string; message: string } {
	if (day.countryCode === "TR") {
		const name = localizedSpecialDayName(day.name, "tr");
		return {
			subject: `${name} Kutlu Olsun`,
			message: `Değerli İş Ortağımız,\n\n${name} vesilesiyle size, ailenize ve ekibinize en içten dileklerimizi sunarız.\n\nSaygılarımızla,`,
		};
	}
	return {
		subject: `Happy ${day.name}`,
		message: `Dear Valued Partner,\n\nOn the occasion of ${day.name}, we extend our warmest wishes to you, your family and your team.\n\nBest regards,`,
	};
}

function parseEmails(value: string): string[] {
	return value
		.split(/[,;\s]+/)
		.map((e) => e.trim())
		.filter(Boolean);
}

function GreetingMailModal({
	day,
	design,
	lang,
	onClose,
}: {
	day: SpecialDay;
	design: SpecialDayDesign;
	lang: string;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const recipientsQuery = useSpecialDayRecipients(day.countryCode);
	const sendMail = useSendSpecialDayEmail();
	const initial = useMemo(() => defaultGreeting(day), [day]);
	const [emails, setEmails] = useState<string | null>(null);
	const [subject, setSubject] = useState(initial.subject);
	const [message, setMessage] = useState(initial.message);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<SendSpecialDayEmailResult | null>(null);

	const data = recipientsQuery.data;
	const emailValue = emails ?? (data?.recipients ?? []).map((r) => r.email).join(", ");
	const emailList = parseEmails(emailValue);
	const countryName = localizedCountryName(day.countryCode, null, lang);
	const dayName = localizedSpecialDayName(day.name, lang);
	const title = t("specialDays.mailTitle");

	const handleSend = async () => {
		setError(null);
		if (emailList.length === 0) {
			setError(t("requiredField"));
			return;
		}
		if (data?.signatureMissingReason) {
			const proceed = window.confirm(`${data.signatureMissingReason}\n\n${t("specialDays.sendAnyway")}`);
			if (!proceed) return;
		}
		if (!window.confirm(t("specialDays.confirmSend", { count: emailList.length, country: countryName }))) {
			return;
		}
		try {
			const outcome = await sendMail.mutateAsync({
				country: day.countryCode,
				designPath: design.path,
				subject: subject.trim(),
				message,
				emails: emailList,
			});
			setResult(outcome);
		} catch (err) {
			setError(err instanceof Error ? err.message : t("specialDays.sendFailed"));
		}
	};

	if (result) {
		return (
			<Modal isOpen onClose={onClose} title={title} maxWidth="560px">
				<div className={styles.mailForm}>
					<p>{t("specialDays.sentCount", { count: result.sent })}</p>
					{result.failed.length > 0 && (
						<div className={styles.mailWarning}>
							<p>{t("specialDays.failedCount", { count: result.failed.length })}</p>
							<ul>
								{result.failed.map((f) => (
									<li key={f.email}>
										<strong>{f.email}</strong> — {f.error}
									</li>
								))}
							</ul>
						</div>
					)}
					<div className={styles.modalActions}>
						<Button onClick={onClose}>{t("close")}</Button>
					</div>
				</div>
			</Modal>
		);
	}

	return (
		<Modal isOpen onClose={onClose} title={title} maxWidth="1100px">
			{recipientsQuery.isLoading ? (
				<LoadingSpinner />
			) : recipientsQuery.error ? (
				<p className={styles.mailWarning}>{(recipientsQuery.error as Error).message}</p>
			) : (
				<div className={styles.mailLayout}>
					<div className={styles.mailForm}>
						<FormField label={t("specialDays.designLabel")}>
							<span>
								{flagEmoji(day.countryCode)} {countryName} · {dayName}
								<span className={styles.mailDesignLang}>{design.language}</span>
							</span>
						</FormField>
						<FormField label={t("email")} required>
							<textarea
								className={styles.textarea}
								rows={3}
								value={emailValue}
								onChange={(e) => setEmails(e.target.value)}
								placeholder={t("emailCcPlaceholder")}
							/>
						</FormField>
						<p className={styles.templateHint}>
							{data?.recipients.length
								? t("specialDays.emailsHint", { count: emailList.length })
								: t("specialDays.noRecipients")}
						</p>
						<FormField label={t("subject")}>
							<Input value={subject} onChange={(e) => setSubject(e.target.value)} />
						</FormField>
						<FormField label={t("message")}>
							<textarea
								className={styles.textarea}
								rows={8}
								value={message}
								onChange={(e) => setMessage(e.target.value)}
							/>
						</FormField>
						{data && !data.mailConfigured && (
							<p className={styles.mailWarning}>{t("specialDays.mailNotConfigured")}</p>
						)}
						{data?.signatureMissingReason && (
							<p className={styles.mailWarning}>{data.signatureMissingReason}</p>
						)}
						{error && <p className={styles.mailError}>{error}</p>}
						<div className={styles.modalActions}>
							<Button variant="ghost" onClick={onClose}>
								{t("cancel")}
							</Button>
							<Button
								onClick={handleSend}
								isLoading={sendMail.isPending}
								disabled={emailList.length === 0 || !data?.mailConfigured}
							>
								{t("send")}
							</Button>
						</div>
					</div>
					<div className={styles.mailPreview}>
						<div className={styles.mailPreviewBody}>
							<img src={designPreviewUrl(design.path)} alt="" className={styles.mailPreviewImage} />
							{message.split("\n").map((line, index) => (
								<p key={index}>{line || "\u00a0"}</p>
							))}
							{!data?.signatureMissingReason && (
								<p className={styles.mailPreviewNote}>{t("specialDays.signatureAdded")}</p>
							)}
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
}
